import { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Award, Tag, Package, Truck, Leaf, Megaphone, Factory, Briefcase, Cpu, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";

const CAT_META = {
  packaging: { label: "Packaging", Icon: Package, tint: "#FFC93C" },
  delivery: { label: "Delivery", Icon: Truck, tint: "#7DD3C0" },
  sustainability: { label: "Sustainability", Icon: Leaf, tint: "#9EE493" },
  marketing: { label: "Marketing", Icon: Megaphone, tint: "#FF4F5A" },
  manufacturing: { label: "Manufacturing", Icon: Factory, tint: "#C9A977" },
  business: { label: "Business", Icon: Briefcase, tint: "#2B2B2B" },
  technology: { label: "Technology", Icon: Cpu, tint: "#7B9EFF" },
  ideas: { label: "Ideas", Icon: Lightbulb, tint: "#F5A623" },
};

const catOf = (k) => CAT_META[k] || { label: k, Icon: Tag, tint: "#2B2B2B" };

export function ChallengesList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/challenges").then((r) => setItems(r.data || [])); }, []);

  const cats = useMemo(() => {
    const s = new Set(items.map((c) => c.category));
    return ["all", ...Array.from(s)];
  }, [items]);

  const visible = useMemo(() => filter === "all" ? items : items.filter((c) => c.category === filter), [items, filter]);
  const featured = visible.find((c) => c.status === "open") || visible[0];
  const rest = visible.filter((c) => c.id !== featured?.id);

  return (
    <div data-testid="challenges-page" className="section-pad">
      <div className="container-pp">
        {/* Editorial Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="editorial-divider mb-5" />
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Open Call · Innovation Challenges</div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="editorial-h1 text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.95]">
              We have <em className="font-display italic">problems</em>.<br />
              We're inviting <em className="font-display italic">solutions</em>.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 text-brand-charcoal/70 leading-relaxed">
            <p>Real, unsolved problems from inside Pop Panda. Packaging that bends in summer heat. Distribution to schools that have no freezer. Marketing on a zero-rupee budget.</p>
            <p className="mt-3">Submit a solution. Best ideas are rewarded — and shipped.</p>
          </div>
        </div>

        {/* Category filter */}
        {cats.length > 2 && (
          <div className="mt-14 flex flex-wrap gap-2" data-testid="challenge-filter">
            {cats.map((k) => {
              const meta = k === "all" ? { label: "All challenges", Icon: Tag } : catOf(k);
              const active = filter === k;
              return (
                <button key={k} data-testid={`ch-filter-${k}`} onClick={() => setFilter(k)}
                  className={`btn-pill text-xs inline-flex items-center gap-1.5 transition-all ${active ? "btn-dark" : "btn-ghost border border-brand-line hover:bg-brand-muted"}`}>
                  <meta.Icon size={12} /> {meta.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Featured spotlight */}
        {featured && (
          <Link to={`/challenges/${featured.id}`} data-testid={`challenge-featured-${featured.id}`}
            className="group relative block mt-10 overflow-hidden rounded-[2rem] bg-brand-charcoal text-brand-cream">
            <div className="absolute inset-0 grain-overlay opacity-30" />
            <div className="absolute -right-32 -top-32 w-[28rem] h-[28rem] rounded-full"
              style={{ background: `radial-gradient(circle at center, ${catOf(featured.category).tint}55, transparent 70%)` }} />
            <div className="relative grid lg:grid-cols-12 gap-8 p-10 lg:p-16">
              <div className="lg:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-brand-yellow animate-pulse" />
                  Featured Challenge
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-cream/55 mt-6 flex items-center gap-2">
                  {(() => { const M = catOf(featured.category); return <><M.Icon size={12} /> {M.label}</>; })()}
                </div>
                <h2 className="font-display text-4xl md:text-6xl mt-3 leading-[1.05]">{featured.title}</h2>
                {featured.brief && <p className="mt-5 text-brand-cream/75 max-w-2xl leading-relaxed">{featured.brief}</p>}
              </div>
              <div className="lg:col-span-4 lg:border-l lg:border-white/15 lg:pl-8 flex flex-col gap-5 justify-end">
                {featured.reward && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cream/45">Reward</div>
                    <div className="font-display text-2xl mt-1 text-brand-yellow">{featured.reward}</div>
                  </div>
                )}
                {featured.deadline && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cream/45">Deadline</div>
                    <div className="font-display text-xl mt-1">{featured.deadline}</div>
                  </div>
                )}
                <span className="inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all w-fit">
                  Submit your solution <ArrowUpRight size={16} />
                </span>
              </div>
            </div>
          </Link>
        )}

        {/* Grid of remaining challenges */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="challenges-grid">
          {rest.map((c, i) => {
            const M = catOf(c.category);
            return (
              <motion.div key={c.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}>
                <Link to={`/challenges/${c.id}`} data-testid={`challenge-${c.id}`} className="block card-elevated overflow-hidden group h-full hover:-translate-y-1 transition-transform duration-300">
                  <div className="h-2 w-full" style={{ background: M.tint }} />
                  <div className="p-7">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5" style={{ color: M.tint === "#FFC93C" ? "#2B2B2B" : M.tint }}>
                        <M.Icon size={12} /> {M.label}
                      </div>
                      <span className={`text-[10px] uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${c.status === "closed" ? "border-brand-charcoal/30 text-brand-charcoal/55" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>{c.status}</span>
                    </div>
                    <h3 className="font-display text-2xl mt-4 leading-tight">{c.title}</h3>
                    {c.brief && <p className="mt-3 text-sm text-brand-charcoal/70 leading-relaxed line-clamp-3">{c.brief}</p>}
                    <div className="mt-5 flex flex-wrap gap-4 text-xs text-brand-charcoal/65">
                      {c.reward && <span className="flex items-center gap-1"><Award size={12} /> {c.reward}</span>}
                      {c.deadline && <span className="flex items-center gap-1"><Calendar size={12} /> {c.deadline}</span>}
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium">View challenge <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
          {items.length === 0 && <div className="text-brand-charcoal/60 col-span-full">No open challenges right now — check back soon.</div>}
        </div>
      </div>
    </div>
  );
}

export function ChallengeDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", headline: "", solution: "", attachment_url: "" });

  useEffect(() => {
    api.get(`/challenges/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(`/challenges/${id}/solutions`, form); setDone(true); toast.success("Solution submitted!"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (notFound) return <Navigate to="/challenges" replace />;
  if (!c) return <div className="section-pad container-pp text-brand-charcoal/60">Loading challenge…</div>;

  const M = catOf(c.category);

  return (
    <div data-testid={`challenge-detail-${c.id}`} className="section-pad">
      <div className="container-pp grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Link to="/challenges" className="text-xs text-brand-charcoal/55 hover:text-brand-charcoal" data-testid="ch-back">← All challenges</Link>
          <div className="text-[11px] uppercase tracking-[0.22em] mt-4 flex items-center gap-1.5" style={{ color: M.tint === "#FFC93C" ? "#2B2B2B" : M.tint }}>
            <M.Icon size={12} /> {M.label}
          </div>
          <h1 className="editorial-h1 text-5xl md:text-6xl mt-3 leading-[1.05]">{c.title}</h1>
          {c.brief && <p className="mt-6 text-brand-charcoal/75 leading-relaxed">{c.brief}</p>}
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {c.reward && (
              <div className="card-elevated p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55 flex items-center gap-1.5"><Award size={12} /> Reward</div>
                <div className="font-display text-xl mt-2 text-brand-red">{c.reward}</div>
              </div>
            )}
            {c.deadline && (
              <div className="card-elevated p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55 flex items-center gap-1.5"><Calendar size={12} /> Deadline</div>
                <div className="font-display text-xl mt-2">{c.deadline}</div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div className="card-elevated p-12" data-testid="challenge-done">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Solution Received</div>
              <h2 className="font-display text-4xl mt-3">Thank you, {form.name.split(" ")[0]}.</h2>
              <p className="mt-4 text-brand-charcoal/70 leading-relaxed">We read every submission carefully. If your idea moves us, our team will reach out within 14 days.</p>
              <Link to="/challenges" className="btn-pill btn-dark text-sm mt-8 inline-flex" data-testid="ch-back-to-all">Browse other challenges <ArrowUpRight size={14} /></Link>
            </div>
          ) : c.status === "closed" ? (
            <div className="card-elevated p-12">
              <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Closed</div>
              <h2 className="font-display text-4xl mt-3">This challenge is closed.</h2>
              <p className="mt-4 text-brand-charcoal/70">Submissions are no longer being accepted. Browse our other open challenges.</p>
            </div>
          ) : (
            <form className="card-elevated p-8 lg:p-10 space-y-6" onSubmit={submit} data-testid="challenge-form">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">Submit your solution</div>
                <div className="font-display text-3xl mt-2">Tell us what you'd do.</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Name</label><input data-testid="ch-name" className="pp-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email (optional)</label><input data-testid="ch-email" type="email" className="pp-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</label><input data-testid="ch-phone" className="pp-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Solution Headline</label><input data-testid="ch-head" className="pp-input" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} /></div>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Your solution</label><textarea data-testid="ch-sol" required rows="6" className="pp-input" value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} /></div>
              <div><label className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Attachment URL (optional — drawings, mockups)</label><input data-testid="ch-att" className="pp-input" value={form.attachment_url} onChange={(e) => setForm({ ...form, attachment_url: e.target.value })} /></div>
              <button data-testid="ch-submit" className="btn-pill btn-dark inline-flex items-center gap-2">Submit Solution <ArrowUpRight size={14} /></button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
